/* eslint-disable */
import {
  FriendlyOrganizationResponseEntity,
  JobResponseEntity,
  Organization,
  OrganizationEntity,
  OrganizationImageEntity,
  OrganizationReviewEntity,
  WpPostEntity
} from "@/hooks/api/generated";
import { extractTermRelationshipByType, getMetaValue } from "./wordpress";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getOrganizationImages } from "./organization";
import { parseContent } from "./parseContent";
import { JobPosting } from "@/src/app/[locale]/(public)/jobs/[id]/Components/JobPosting";

dayjs.extend(utc);
dayjs.extend(timezone);

export const APP_URL = process.env.NEXT_PUBLIC_APP_URL || "https://mygwork.com";
export const LOGO_URL =
  "https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1752151188/web-app-images/mygwork-logo_ojurkz.png";

/**
 * Strips HTML tags from a string.
 */
const stripHtml = (html: string): string => {
  if (!html) {
    return "";
  }
  return html.replace(/<[^>]*>?/gm, "").trim();
};

/**
 * Gets the first paragraph or a summary of a string after stripping HTML.
 */
const getSummary = (html: string): string => {
  const stripped = stripHtml(html).trim();
  let summary = stripped.split("\n")[0] || stripped;

  if (summary.length < 255) {
    summary = stripped;
  }

  if (summary.length > 255) {
    return summary.substring(0, 252);
  }

  return summary;
};

/**
 * Platform Organization Schema
 */
export const getPlatformSchema = ({
 minimal = false,
 includeBasicInfo = false,
}: {
  minimal?: boolean;
  includeBasicInfo?: boolean;
} = {}) => {
  const schema: any = {
    "@type": "Organization",
    "@id": `${APP_URL}/#platform`,
    name: "myGwork",
  };

  if (minimal) {
    return schema;
  }

  if (includeBasicInfo) {
    return {
      ...schema,
      url: APP_URL,
      logo: LOGO_URL,
      email: "support@mygwork.com",
    };
  }

  return {
    ...schema,
    url: APP_URL,
    logo: LOGO_URL,
    email: "support@mygwork.com",
    description:
      "The largest global business community for LGBTQ+ professionals, graduates, and inclusive employers.",
    knowsAbout: [
      "DEI",
      "Inclusive Hiring",
      "LGBTQ+ Recruitment",
      "Workplace Equality",
    ],
    sameAs: [
      "https://www.linkedin.com/company/mygwork",
      "https://twitter.com/mygwork",
      "https://www.facebook.com/mygwork",
      "https://www.instagram.com/mygwork",
    ],
  };
};

/**
 * WebSite Schema
 */
export const getWebSiteSchema = (minimal: boolean = false) => {
  const schema: any = {
    "@type": "WebSite",
    "@id": `${APP_URL}/#website`,
    url: APP_URL,
    name: "myGwork",
    publisher: {
      "@id": `${APP_URL}/#platform`,
    },
  };

  if (minimal) {
    return schema;
  }

  return {
    ...schema,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${APP_URL}/jobs?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
};

/**
 * BreadcrumbList Schema
 */
export const getBreadcrumbSchema = (
  items: { name: string; url: string }[],
  id: string = "/#breadcrumb"
) => ({
  "@type": "BreadcrumbList",
  "@id": `${APP_URL}${id}`,
  name: "Breadcrumbs",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url.startsWith("http") ? item.url : `${APP_URL}${item.url}`,
  })),
});

/**
 * WebPage Schema
 */
export const getWebPageSchema = (name: string, url: string = "", type: string = "WebPage") => ({
  "@type": type,
  "@id": `${APP_URL}${url}#webpage`,
  url: `${APP_URL}${url.replace(/\/$/, '')}`,
  name,
  isPartOf: {
    "@id": `${APP_URL}/#website`,
  },
  breadcrumb: {
    "@id": `${APP_URL}${url}#breadcrumb`,
  },
  about: {
    "@id": `${APP_URL}${url}#platform`,
  },
  mainEntity: {
    "@id": `${APP_URL}${url}#platform`,
  },
});

export function createOrganizationRatingSchema(organization: { rating: number, total_reviews: number, name:string, slug:string }) {
  if (organization.rating && organization.total_reviews) {
    return {
      "@type": "EmployerAggregateRating",
      itemReviewed: {
        "@type": "Organization",
        name: organization.name,
        sameAs: `${ APP_URL }/organizations/${ organization.slug }`,
      },
      ratingValue: organization.rating,
      bestRating: "5",
      worstRating: "1",
      ratingCount: organization.total_reviews,
    };
  }
}

/**
 * Employer Organization Schema
 */
export const getEmployerSchema = (org: FriendlyOrganizationResponseEntity) => {
  const logo = org.organizationImage?.find((img) => img?.image?.type === "logo")
    ?.image?.name;

  const schema: any = {
    "@type": "Organization",
    "@id": `${APP_URL}/organizations/${org.slug}/#employer`,
    name: org.name,
    url: `${APP_URL}/organizations/${org.slug}`,
    memberOf: {
      "@id": `${APP_URL}/#platform`,
    },
  };

  if (logo) {
    schema.logo = logo;
  }

  let description =
    org.meta_description ||
    getSummary(org.organization_info?.post_content || "");
  if (description) {
    description = description.substring(0, 255);
    schema.description = description;
  }

  schema.aggregateRating = createOrganizationRatingSchema({
    name: org.name,
    rating: org.rating,
    total_reviews: org.total_reviews,
    slug: org.slug
  });

  return schema;
};

export function getSocialMediaLinks(org: Organization | OrganizationEntity) {
  return [
    org.twitter_url,
    org.linkedin_url,
    org.youtube_url,
    org.facebook_url,
    org.instagram_url,
  ].filter(Boolean);
}

/**
 * Organization Employer Schema (for specific Organization page)
 */
export const getOrganizationEmployerSchema = (
  org: Organization | OrganizationEntity,
  postContent?: string,
  minimal: boolean = false
) => {
  const { logoImageUrl, coverImageUrl } = getOrganizationImages(org, true);
  const organization_logo_url = logoImageUrl
    ? `https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1/${logoImageUrl}`
    : LOGO_URL;
  const organization_banner_url = coverImageUrl
    ? `https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1/${coverImageUrl}`
    : undefined;

  const organization_summary = postContent ? getSummary(postContent) : "";

  const sameAs = getSocialMediaLinks(org);

  const schema: any = {
    "@type": "Organization",
    "@id": `${APP_URL}/organizations/${org.slug}/#employer`,
    name: org.name,
    url: `${APP_URL}/organizations/${org.slug}`,
    memberOf: {
      "@id": `${APP_URL}/#platform`,
    },
  };

  if (minimal) {
    if (organization_logo_url) {
      schema.logo = organization_logo_url;
    }
    if (organization_banner_url) {
      schema.image = organization_banner_url;
    }
    if (org.industry?.name) {
      schema.industry = org.industry.name;
    }
    return schema;
  }

  schema.logo = organization_logo_url;
  schema.sameAs = sameAs;

  if (organization_banner_url) {
    schema.image = organization_banner_url;
  }

  if (org.industry?.name) {
    schema.industry = org.industry.name;
  }

  if (organization_summary) {
    schema.description = organization_summary;
  }

  // EmployerAggregateRating (nested on the Organization, per schema.org). Only
  // emitted when there is a real rating backed by at least one review.
  const ratedOrg = org as Organization;
  const reviewCount = ratedOrg.navigationCounts?.reviews;
  if (ratedOrg.rating && reviewCount) {
    schema.aggregateRating = {
      "@type": "EmployerAggregateRating",
      ratingValue: ratedOrg.rating,
      bestRating: "5",
      worstRating: "1",
      ratingCount: reviewCount,
    };
  }

  return schema;
};

/**
 * ProfilePage Schema
 */
export const getProfilePageSchema = (org: OrganizationEntity) => ({
  "@type": "ProfilePage",
  "@id": `${APP_URL}/organizations/${org.slug}/#webpage`,
  url: `${APP_URL}/organizations/${org.slug}`,
  name: `${org.name} - Inclusive Employer Profile`,
  isPartOf: {
    "@id": `${APP_URL}/#website`,
  },
  breadcrumb: {
    "@id": `${APP_URL}/organizations/${org.slug}/#breadcrumb`,
  },
  mainEntity: {
    "@id": `${APP_URL}/organizations/${org.slug}/#employer`,
  },
});

/**
 * CollectionPage Schema for Organization Directory
 */
export const getCollectionPageSchema = (
  name: string,
  url: string,
  description: string,
  organizations: OrganizationEntity[],
  totalItems: number,
  page: number = 1,
  pageSize: number = 12,
  dateModified?: string
) => {
  const baseUrl = `${APP_URL}${url.replace(/\/$/, "")}`;
  const currentUrl = page === 1 ? baseUrl : `${baseUrl}?page=${page}`;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Only first, previous, current, next and last pages are surfaced as
  // hasPart nodes (deduped, in-range) — capped at 5 entries — to keep the
  // JSON-LD payload lean instead of emitting one WebPage per page.
  const hasPartPages = Array.from(
    new Set(
      [1, page - 1, page, page + 1, totalPages].filter(
        (pageNum) => pageNum >= 1 && pageNum <= totalPages
      )
    )
  ).sort((a, b) => a - b);

  const schema: any = {
    "@type": "CollectionPage",
    "@id": `${currentUrl}#webpage`,
    url: currentUrl,
    name,
    description,
    "inLanguage": "en",
    ...(dateModified ? { "dateModified": dateModified } : {}),
    isPartOf: { "@id": `${APP_URL}/#website` },
    breadcrumb: { "@id": `${APP_URL}${url}/#breadcrumb` },
    "hasPart": hasPartPages.map((pageNum) => {
      const pageUrl = pageNum === 1 ? baseUrl : `${baseUrl}?page=${pageNum}`;
      return {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        "url": pageUrl,
      };
    }),
    mainEntity: {
      "@type": "ItemList",
      "name": "Inclusive Employers",
      "numberOfItems": totalItems,
      "itemListOrder": "https://schema.org/ItemListUnordered",
      "itemListElement": organizations.map((org, index) => {
        const { logoImageUrl } = getOrganizationImages(org, true);
        const logo = logoImageUrl
          ? `https://res.cloudinary.com/dv6hrcluu/image/upload/f_auto/q_auto/v1/${logoImageUrl}`
          : undefined;
        const socialLinks = getSocialMediaLinks(org);

        return {
          "@type": "ListItem",
          position: (page - 1) * pageSize + index + 1,
          item: {
            "@type": "Organization",
            "@id": `${APP_URL}/organizations/${org.slug}/#employer`,
            "name": org.name,
            "url": `${APP_URL}/organizations/${org.slug}`,
            "description": org.meta_description || org.name,
            ...(logo && { "logo": logo }),
            "knowsAbout": [org.industry?.name],
            ...(socialLinks?.length ? { sameAs: socialLinks } : {}),
          },
        };
      }),
    },
  };

  if (page > 1 || page < totalPages) {
    schema.potentialAction = [];
    if (page > 1) {
      const prevUrl = page - 1 === 1 ? baseUrl : `${baseUrl}?page=${page - 1}`;
      schema.potentialAction.push({
        "@type": "ViewAction",
        name: "Previous Page",
        target: prevUrl,
      });
    }
    if (page < totalPages) {
      const nextUrl = `${baseUrl}?page=${page + 1}`;
      schema.potentialAction.push({
        "@type": "ViewAction",
        name: "Next Page",
        target: nextUrl,
      });
    }
  }

  return schema;
};

/**
 * ItemList Schema for Organizations
 */
export const getOrganizationCarouselSchema = (
  organizations: OrganizationEntity[],
  name: string = "They work with us",
  id: string = `${APP_URL}/#organization-carousel`
) => ({
  "@type": "ItemList",
  ...(id ? { "@id": id } : {}),
  name: name,
  url: `${APP_URL}/organizations`,
  itemListElement: organizations.map((org, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Organization",
      "@id": `${APP_URL}/organizations/${org.slug}/#employer`,
      name: org.name,
      url: `${APP_URL}/organizations/${org.slug}`,
    },
  })),
});

/**
 * ItemList Schema for Jobs
 */
export const getFeaturedJobsCarouselSchema = (
  orgName: string,
  orgSlug: string,
  jobs: JobResponseEntity[]
) => {
  return {
    "@type": "ItemList",
    "@id": `${APP_URL}/#featured-jobs`,
    name: `Featured Jobs at ${orgName}`,
    itemListElement: jobs.map((job, index) => {
      // Use the JobPosting component to get the schema object
      const jobSchema = JobPosting({
        jobDetails: job,
        returnData: true,
      });

      // Override hiringOrganization ID to point to the employer in the graph
      jobSchema.hiringOrganization = {
        "@id": `${APP_URL}/organizations/${orgSlug}/#employer`,
      };

      return {
        "@type": "ListItem",
        position: index + 1,
        item: jobSchema,
      };
    }),
  };
};

/**
 * ItemList Schema for Reviews
 */
export const getFeaturedReviewsCarouselSchema = (
  orgName: string,
  orgSlug: string,
  reviews: any[]
) => ({
  "@type": "ItemList",
  "@id": `${APP_URL}/#featured-reviews`,
  name: `Reviews for ${orgName}`,
  url: `${APP_URL}/organizations/${orgSlug}/reviews`,
  itemListElement: reviews.map((review, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Review",
      "@id": `${APP_URL}/#review-${review.id}`,
      reviewBody: stripHtml(review.review || review.review_text || ""),
      author: {
        "@type": "Person",
        name: "Verified Member",
      },
      itemReviewed: {
        "@id": `${APP_URL}/organizations/${orgSlug}/#employer`,
      },
      reviewRating: {
        "@type": "Rating",
        ratingValue: review.rating || review.review_rating || 5,
      },
      datePublished: review.createdAt || review.review_date,
    },
  })),
});

/**
 * Generates a schema representation for a news carousel using structured data.
 *
 * @param {Array} newsItems - Array of news items to be included in the carousel.
 * Each item in the array should contain necessary fields such as title, slug,
 * publication date, and optional thumbnail data.
 *
 * @returns {Object} - Structured data schema for the news carousel, following
 * Schema.org specifications. The schema includes an ItemList with a list of
 * NewsArticle items, where each news article includes metadata such as `headline`,
 * `url`, and `datePublished`. Optionally includes an `image` property if a
 * thumbnail is present.
 */
export const getNewsCarouselSchema = (newsItems: WpPostEntity[]) => ({
  "@type": "ItemList",
  "@id": `${APP_URL}/#mygwork-news`,
  name: "myGwork News",
  url: `${APP_URL}/news`,
  itemListElement: newsItems.map((news, index) => {
    const newsSchema: any = {
      "@type": "NewsArticle",
      "@id": `${APP_URL}/news/${news.post_name || news.slug}/#news`,
      headline: news.post_title || news.title,
      url: `${APP_URL}/news/${news.post_name || news.slug}`,
      datePublished: news.post_date || news.published_at,
    };

    const thumbnail =
      getMetaValue(news, "_cloudinary") ||
      news.thumbnail_url ||
      news.news_thumbnail_url;
    if (thumbnail) {
      newsSchema.image = thumbnail;
    }

    return {
      "@type": "ListItem",
      position: index + 1,
      item: newsSchema,
    };
  }),
});

/**
 * Creates a schema representation for news articles in the form of an ItemList with nested NewsArticle items.
 *
 * @param {WpPostEntity[]} newsItems - Array of news article objects containing the necessary data for schema generation.
 * @param {Object} [options] - Optional configuration for customizing the schema.
 * @param {string} [options.name="News Articles"] - Name of the ItemList to be generated.
 * @param {string} [options.authorId] - ID of the author for the news articles, used for linking author schema.
 * @param {string} [options.publisherId] - ID of the publisher for the news articles, used for linking publisher schema.
 * @param {number|string} [options.numberOfItems] - Number of news articles represented in the schema.
 * @returns {Object} - A schema object conforming to the structure for ItemList and NewsArticle as defined in Schema.org.
 */
export const getNewsArticlesSchema = (
  newsItems: WpPostEntity[],
  options: {
    name?: string;
    authorId?: string;
    publisherId?: string;
    numberOfItems?: number | string;
    id?: string;
  } = {}
) => {
  const {
    name = "News Articles",
    authorId,
    publisherId,
    numberOfItems,
    id,
  } = options;

  const formatDate = (date: any) => {
    if (!date) return undefined;
    const d = dayjs(date);
    return d.isValid() ? d.toISOString() : date;
  };

  return {
    "@type": "ItemList",
    ...(id ? { "@id": id.startsWith("http") ? id : APP_URL + id } : {}),
    name,
    ...(numberOfItems !== undefined ? { numberOfItems } : {}),
    itemListElement: newsItems.map((news, index) => {
      const slug = news.post_name || news.slug;
      const newsSchema: any = {
        "@type": "NewsArticle",
        "@id": `${APP_URL}/news/${slug}/#news`,
        headline: parseContent(news.post_title || news.title),
        url: `${APP_URL}/news/${slug}`,
        datePublished: formatDate(news.post_date || news.published_at),
      };

      const thumbnail =
        getMetaValue(news, "_cloudinary") ||
        news.thumbnail_url ||
        news.news_thumbnail_url;
      if (thumbnail) {
        newsSchema.image = thumbnail;
      }

      if (authorId) {
        newsSchema.author = {
          "@id": authorId.startsWith("http") ? authorId : APP_URL + authorId,
        };
      }

      newsSchema.publisher = publisherId
        ? {
            "@id": publisherId.startsWith("http")
              ? publisherId
              : APP_URL + publisherId,
          }
        : { "@id": `${APP_URL}/#platform` };

      const categories = extractTermRelationshipByType(news, "category");
      if (categories && categories.length > 0) {
        newsSchema.articleSection = categories[0]?.name;
      }

      newsSchema.mainEntityOfPage = {
        "@type": "WebPage",
        "@id": `${APP_URL}/news/${slug}`,
      };

      return {
        "@type": "ListItem",
        position: index + 1,
        item: newsSchema,
      };
    }),
  };
};

/**
 * Generates a schema-compliant structure for an events carousel.
 *
 * @param {WpPostEntity[]} events - Array of event objects, each representing an event to be included in the carousel.
 * @returns {Object} An object representing a schema.org-compliant `ItemList`, which includes metadata for each event
 * in the `itemListElement` array. Each event is described using the `Event` schema.
 *
 * The function processes each event to extract and structure its metadata, including:
 * - Event name, URL, and description.
 * - Start date and time, formatted in ISO 8601.
 * - Location information, either as a physical place or a virtual location.
 *
 * This schema is suitable for enhancing SEO and providing structured data for the events carousel.
 */
export const getEventsCarouselSchema = (events: WpPostEntity[]) => ({
  "@type": "ItemList",
  "@id": `${APP_URL}/#mygwork-events`,
  name: "myGwork Events",
  url: `${APP_URL}/events`,
  itemListElement: events.map((event, index) => {
    const startDate = getMetaValue(event, "event_date");
    const startTime = getMetaValue(event, "event_time");
    const location = getMetaValue(event, "event_location");
    const description =
      getMetaValue(event, "summary") || event.post_content || event.description;

    const eventSchema: any = {
      "@type": "Event",
      "@id": `${APP_URL}/events/${event.post_name || event.slug}/#event`,
      name: event.post_title || event.title,
      url: `${APP_URL}/events/${event.post_name || event.slug}`,
    };

    if (startDate) {
      const formattedDate = dayjs(startDate.split("T")[0], "DDMMYYYY").format(
        "YYYY-MM-DD"
      );
      // Combine date and time if available
      eventSchema.startDate = startTime
        ? `${formattedDate}T${startTime}`
        : formattedDate;
    }

    if (description) {
      eventSchema.description = stripHtml(description);
    }

    if (location) {
      eventSchema.location = {
        "@type": "Place",
        name: location,
        address: location,
      };
    } else {
      eventSchema.location = {
        "@type": "VirtualLocation",
        url: `${APP_URL}/events/${event.post_name || event.slug}`,
      };
    }

    return {
      "@type": "ListItem",
      position: index + 1,
      item: eventSchema,
    };
  }),
});

const WORKPRIDE_BANNER =
  "https://res.cloudinary.com/dv6hrcluu/image/upload/workpride/workpride-2026-banner_gcf7a6.png";

/**
 * Builds the Event JSON-LD for the WorkPride conference landing page. Emits a
 * top-level `Event` (the conference) with the individual sessions as `subEvent`
 * entries and the speakers as `performer` Persons. Dates are derived from the
 * session `event_date` metas (falling back to the known 2026 window).
 */
export const getWorkPrideSchema = (data: {
  eventDates?: string;
  events?: unknown;
  speakers?: Array<{ first_name?: string; last_name?: string; slug?: string }>;
}) => {
  const eventsByDay = (data.events || {}) as Record<string, WpPostEntity[]>;
  const sessions: WpPostEntity[] = Object.values(eventsByDay).flat();

  const sessionDates: string[] = [];

  const subEvents = sessions.map((event) => {
    const slug = event.post_name || event.slug;
    const startDateRaw = getMetaValue(event, "event_date");
    const startTime = getMetaValue(event, "event_time");
    const description =
      getMetaValue(event, "summary") || event.post_content || event.description;

    const subEvent: any = {
      "@type": "Event",
      "@id": `${APP_URL}/events/${slug}/#event`,
      name: parseContent(event.post_title || event.title || ""),
      url: `${APP_URL}/events/${slug}`,
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
      eventStatus: "https://schema.org/EventScheduled",
      location: {
        "@type": "VirtualLocation",
        url: `${APP_URL}/events/${slug}`,
      },
    };

    if (startDateRaw) {
      const formattedDate = dayjs(startDateRaw.split("T")[0], "DDMMYYYY").format(
        "YYYY-MM-DD"
      );
      sessionDates.push(formattedDate);
      subEvent.startDate = startTime
        ? `${formattedDate}T${startTime}`
        : formattedDate;
    }

    if (description) {
      subEvent.description = stripHtml(description);
    }

    return subEvent;
  });

  const performers = (data.speakers || [])
    .map((speaker) => ({
      "@type": "Person",
      name: [speaker.first_name, speaker.last_name].filter(Boolean).join(" "),
      ...(speaker.slug ? { url: `${APP_URL}/profile/${speaker.slug}` } : {}),
    }))
    .filter((person) => person.name);

  const sortedDates = sessionDates.filter(Boolean).sort();
  const startDate = sortedDates[0] || "2026-06-08";
  const endDate = sortedDates[sortedDates.length - 1] || "2026-06-12";

  const schema: any = {
    "@type": "Event",
    "@id": `${APP_URL}/workpride/#event`,
    name: "WorkPride 2026",
    description:
      "WorkPride is the largest virtual global conference dedicated to workplace inclusion — a free, award-winning five-day event series for LGBTQ+ professionals, graduates, inclusive employers, and allies.",
    startDate,
    endDate,
    eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    image: WORKPRIDE_BANNER,
    url: `${APP_URL}/workpride`,
    organizer: { "@id": `${APP_URL}/#platform` },
    location: {
      "@type": "VirtualLocation",
      url: `${APP_URL}/workpride`,
    },
  };

  if (performers.length) {
    schema.performer = performers;
  }

  if (subEvents.length) {
    schema.subEvent = subEvents;
  }

  return schema;
};

/**
 * Generates a schema-conforming object for a list of events using the Schema.org
 * specifications, including event metadata and location details.
 *
 * @param {WpPostEntity[]} events - An array of event objects. Each object represents a single event
 *                         and should contain relevant metadata such as event title, dates,
 *                         times, location, and descriptions.
 * @param {Object} [options] - Optional configuration options for the schema generation.
 * @param {string} [options.name="myGwork Events"] - The name of the item list. Defaults to "myGwork Events".
 * @param {string} [options.organizerId] - An optional organizer ID to associate with the events.
 *                                         If provided, it's added to the schema as an organizer reference.
 * @param {number|string} [options.numberOfItems] - The number of items in the event list, added to the
 *                                                  schema if specified.
 * @returns {Object} - A JSON object conforming to Schema.org's `ItemList` specification, including the
 *                     list of events and metadata.
 */
export const getEventsSchema = (
  events: WpPostEntity[],
  options: {
    name?: string;
    organizerId?: string;
    slug?: string;
    numberOfItems?: number | string;
    isPanel?: boolean;
    organizationId?: number,
  } = {}
) => {
  const {
    name = "myGwork Events",
    organizerId,
    slug,
    numberOfItems,
    isPanel = false,
    organizationId,
  } = options;

  return {
    "@type": "ItemList",
    name,
    ...(numberOfItems !== undefined ? { numberOfItems } : {}),

    itemListElement: events.map((event, index) => {
      const startDate = getMetaValue(event, "event_date");
      const startTime = getMetaValue(event, "event_time");
      const endDateValue = getMetaValue(event, "event_end_date");
      const endTimeValue = getMetaValue(event, "event_end_time");
      const location = getMetaValue(event, "event_location");
      const sponsorIdsMeta = getMetaValue(event, "sponsored_by_organizations");
      const sponsorIds =
        sponsorIdsMeta?.match(/"(\d+)"/g)?.map((m) => m.replace(/"/g, "")) ?? [];
      const isSponsor = sponsorIds.includes(String(organizationId));
      if (isSponsor) {
        console.log("Organization exists in sponsorIds");
      }
      const description =
        getMetaValue(event, "summary") ||
        event.post_content ||
        event.description;

      const formatSchemaDate = (date: string, time?: string) => {
        if (!date) {
          return undefined;
        }
        const d = dayjs(date.split("T")[0], "DDMMYYYY");
        if (!d.isValid()) {
          return undefined;
        }
        const formattedDate = d.format("YYYY-MM-DD");
        return time ? `${formattedDate}T${time}` : formattedDate;
      };

      const eventSchema: any = {
        "@type": "Event",
        "@id": `${APP_URL}/events/${event.post_name}/#event`,
        name: parseContent(event.post_title || event.title),
        url: `${APP_URL}/events/${event.post_name}`,
        eventStatus: "https://schema.org/EventScheduled",
      };

      const startDateFormatted = formatSchemaDate(startDate, startTime);
      if (startDateFormatted) {
        eventSchema.startDate = startDateFormatted;
      }

      const endDateFormatted = formatSchemaDate(endDateValue, endTimeValue);
      if (endDateFormatted) {
        eventSchema.endDate = endDateFormatted;
      }

      if (description) {
        eventSchema.description = stripHtml(description);
      }

      const image = getMetaValue(event, "_cloudinary");
      if (image) {
        eventSchema.image = image;
      }

      if (organizerId && !isPanel) {
        eventSchema.organizer = { "@id": APP_URL + organizerId };
      }


      if (location && location.toLowerCase() !== "online") {
        eventSchema.eventAttendanceMode =
          "https://schema.org/OfflineEventAttendanceMode";
        eventSchema.location = {
          "@type": "Place",
          name: location,
          address: location,
        };
      } else {
        eventSchema.eventAttendanceMode =
          "https://schema.org/OnlineEventAttendanceMode";
        eventSchema.location ={
          "@type": "VirtualLocation", // When this, not display in the Rich Result Test
          url: `${APP_URL}/events/${event.post_name}`,
        }
      }

      if (isPanel) {
        if (event.organizer) {
          eventSchema.organizer = {
            "@type": "Organization",
            name: event.organizer.name,
            url: `${APP_URL}/organizations/${event.organizer.slug}`,
          };
        }

        const visibilityReasons = event.panel_post_visibility_reasons ?? [];

        if (visibilityReasons.includes("sponsor")) {
          eventSchema.sponsor = {
            "@id": `${APP_URL}/organizations/${options.slug}/#employer`,
          };
        }

        const panelSpeakers = event.panel_speakers_from_org ?? [];
        if (panelSpeakers.length > 0) {
          eventSchema.performer = panelSpeakers.map(
            (speaker: {
              id: number;
              firstName?: string;
              lastName?: string;
              slug?: string;
              jobTitle?: string;
              imageUrl?: string;
            }) => {
              const fullName = `${speaker.firstName ?? ""} ${
                speaker.lastName ?? ""
              }`.trim();
              const profileUrl = speaker.slug
                ? `${APP_URL}/profile/${speaker.slug}`
                : undefined;
              return {
                "@type": "Person",
                name: fullName || `Speaker ${speaker.id}`,
                ...(profileUrl ? { url: profileUrl } : {}),
                ...(speaker.jobTitle ? { jobTitle: speaker.jobTitle } : {}),
                ...(speaker.imageUrl ? { image: speaker.imageUrl } : {}),
                affiliation: {
                  "@id": `${APP_URL}/organizations/${options.slug}/#employer`,
                },
              };
            }
          );
        }
      }
      eventSchema.offer = {
        "@type": "Offer",
        url: "https://mygwork.com/events/"+event.post_name,
        price: "0",
        priceCurrency: "GBP",
        availability: "https://schema.org/InStock",
        validFrom: dayjs(event.post_date).format("YYYY-MM-DD"),
      }
      return {
        "@type": "ListItem",
        position: index + 1,
        item: eventSchema,
      };
    }),
  };
};

/**
 * CollectionPage Schema for Organization Jobs Tabs
 */
export const getJobsCollectionPageSchema = (
  org: OrganizationEntity | Organization,
  jobs: any[],
  totalJobs: number,
  tabType: string
) => {
  const tabNames: Record<string, string> = {
    jobs: `Current Openings at ${org.name}`,
    "early-careers": `Early Career Opportunities at ${org.name}`,
    internships: `Internships at ${org.name}`,
    apprenticeships: `Apprenticeships at ${org.name}`,
  };

  const name = tabNames[tabType] || `Jobs at ${org.name}`;
  const url = `${APP_URL}/organizations/${org.slug}/${tabType}`;

  return {
    "@type": "CollectionPage",
    "@id": `${url}/#webpage`,
    url,
    isPartOf: { "@id": `${APP_URL}/#website` },
    breadcrumb: { "@id": `${url}/#breadcrumb` },
    about: { "@id": `${APP_URL}/organizations/${org.slug}/#employer` },
    mainEntity: {
      "@type": "ItemList",
      name,
      numberOfItems: totalJobs,
      itemListElement: jobs.map((job, index) => {
        const jobSchema = JobPosting({ jobDetails: job, returnData: true });

        // Ensure JobPosting uses the #job suffix as required
        jobSchema["@id"] = `${APP_URL}/jobs/${job.slug}/#job`;

        // Override hiringOrganization to use the ID reference
        jobSchema.hiringOrganization = {
          "@id": `${APP_URL}/organizations/${org.slug}/#employer`,
        };

        return {
          "@type": "ListItem",
          position: index + 1,
          item: jobSchema,
        };
      }),
      potentialAction: {
        "@type": "ViewAction",
        name: "View all jobs",
        target: `${APP_URL}/jobs`,
      },
    },
  };
};

/**
 * Generates a schema for an organization collection page.
 *
 * @param {OrganizationEntity | Organization} org - The organization object containing details such as the slug.
 * @param {string} tabType - The tab type or section of the organization that the page represents.
 * @param {any} mainEntity - The primary entity that the collection page is about.
 * @param {Object} [options] - Optional additional properties to merge into the schema.
 * @returns {Object} A schema object representing the organization collection page.
 */
export const getOrganizationCollectionPageSchema = (
  org: OrganizationEntity | Organization,
  tabType: string,
  mainEntity: any,
  options?:{}
) => {
  const url = `${APP_URL}/organizations/${org.slug}/${tabType}`;
  return {
    "@type": "CollectionPage",
    "@id": `${url}/#webpage`,
    url,
    isPartOf: { "@id": `${APP_URL}/#website` },
    breadcrumb: { "@id": `${url}/#breadcrumb` },
    about: { "@id": `${APP_URL}/organizations/${org.slug}/#employer` },
    mainEntity,
    ...options,
  };
};

/**
 * CollectionPage Schema for Job Search Results
 */
export const getJobsSearchCollectionPageSchema = (
  jobs: any[],
  totalJobs: number
) => {
  const url = "/jobs";
  return {
    "@type": "CollectionPage",
    "@id": `${APP_URL}${url}/#webpage`,
    url: `${APP_URL}${url}`,
    name: "Search LGBTQ+ Inclusive Jobs",
    description:
      "Browse and filter through thousands of jobs from inclusive employers worldwide.",
    isPartOf: { "@id": `${APP_URL}/#website` },
    breadcrumb: { "@id": `${APP_URL}${url}/#breadcrumb` },
    mainEntity: {
      "@type": "ItemList",
      name: "Job Search Results",
      numberOfItems: totalJobs,
      itemListElement: jobs.map((job, index) => {
        const jobSchema = JobPosting({ jobDetails: job, returnData: true });

        // Ensure JobPosting uses the #job suffix as required
        jobSchema["@id"] = `${APP_URL}/jobs/${job.slug}/#job`;

        // Override hiringOrganization to use the ID reference
        jobSchema.hiringOrganization = {
          "@id": `${APP_URL}/organizations/${job.organization.slug}/#employer`,
        };

        return {
          "@type": "ListItem",
          position: index + 1,
          item: jobSchema,
        };
      }),
      potentialAction: {
        "@type": "ViewAction",
        name: "View all jobs",
        target: `${APP_URL}/jobs`,
      },
    },
  };
};

/**
 * Generates a schema-compliant representation of organization reviews as an ItemList.
 *
 * @param {string} orgSlug - The unique slug identifier for the organization.
 * @param {OrganizationReviewEntity[]} reviews - An array of review entities for the organization.
 * @param {Object} [options={}] - Configuration options for the schema generation.
 * @param {string} [options.name="Reviews"] - The name of the ItemList.
 * @param {number|string} [options.numberOfItems] - The total number of items in the list.
 * @returns {Object} A JSON-LD representation of the reviews, formatted according to schema.org specifications.
 */
export const getReviewsSchema = (
  orgSlug: string,
  reviews: OrganizationReviewEntity[],
  options: {
    name?: string;
    numberOfItems?: number | string;
  } = {}
) => {
  const { name = "Reviews", numberOfItems } = options;

  return {
    "@type": "ItemList",
    name,
    ...(numberOfItems !== undefined ? { numberOfItems } : {}),
    itemListElement: reviews.map((review, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Review",
        "@id": `${APP_URL}/organizations/${orgSlug}/reviews/#review-${review.id}`,
        author: {
          "@type": "Person",
          name: "Anonymous Member",
        },
        datePublished: review.createdAt,
        reviewBody: stripHtml(review.review || ""),
        reviewRating: {
          "@type": "Rating",
          ratingValue: review.rating || 5,
          bestRating: "5",
          worstRating: "1",
        },
        itemReviewed: {
          "@id": `${APP_URL}/organizations/${orgSlug}/#employer`,
        },
      },
    })),
  };
};

/**
 * Generates a schema for an organization collection page.
 *
 * @param {OrganizationEntity | Organization} org - The organization object containing details such as the slug.
 * @param {string} tabType - The tab type or section of the organization that the page represents.
 * @param {any} mainEntity - The primary entity that the collection page is about.
 * @param {Object} [options] - Optional additional properties to merge into the schema.
 * @returns {Object} A schema object representing the organization collection page.
 */
export const getEventsListCollectionPageSchema = (
  mainEntity: any,
  options?:{}
) => {
  const url = `${APP_URL}/events`;
  return {
    "@type": "CollectionPage",
    "@id": `${url}/#webpage`,
    url,
    name: "LGBTQ+ Business & Professional Events | myGwork",
    description: "Discover upcoming LGBTQ+ professional events, networking opportunities, and webinars hosted by myGwork and our partners.",
    isPartOf: { "@id": `${APP_URL}/#website` },
    breadcrumb: { "@id": `${url}/#breadcrumb` },
    mainEntity,
    ...options,
  };
};

/**
 * Event Detail Schema
 */
export const getEventDetailSchema = (
  event: WpPostEntity,
  options: {
    speakers?: any[];
    sponsors?: any[];
  } = {}
) => {
  const { speakers = [], sponsors = [] } = options;
  const slug = event.post_name || event.slug;
  const title = parseContent(event.post_title || event.title);
  const description =
    getMetaValue(event, "summary") || event.post_content || event.description;
  const bannerUrl = getMetaValue(event, "_cloudinary") || LOGO_URL;

  const startDateValue = getMetaValue(event, "event_date");
  const startTimeValue = getMetaValue(event, "event_time");
  const endDateValue = getMetaValue(event, "event_end_date");
  const endTimeValue = getMetaValue(event, "event_end_time");

  const formatSchemaDate = (date: string, time?: string) => {
    if (!date) {
      return undefined;
    }
    const cleanDate = date.split("T")[0];
    const cleanTime = time ? time.replace(/:/g, "") : "000000";
    
    // Try to parse with common WP formats
    const d = dayjs(`${cleanDate}${cleanTime}`, [
      "DDMMYYYYHHmmss",
      "YYYYMMDDHHmmss",
      "YYYY-MM-DDHHmmss"
    ]);

    if (!d.isValid()) {
      return undefined;
    }

    return d.format("YYYY-MM-DDTHH:mm:ssZ");
  };

  const startDate = formatSchemaDate(startDateValue, startTimeValue);
  let endDate = formatSchemaDate(endDateValue, endTimeValue);

  if(!endDate){
    endDate = dayjs(startDate).add(1, "hour").format("YYYY-MM-DDTHH:mm:ssZ");
  }
  const location = getMetaValue(event, "event_location");

  const eventSchema: any = {
    "@type": "Event",
    "@id": `${APP_URL}/events/${slug}/#event`,
    name: title,
    description: stripHtml(description),
    image: bannerUrl,
    startDate,
    endDate,
    eventStatus: "https://schema.org/EventScheduled",
    url: `${APP_URL}/events/${slug}`,
    organizer: { "@id": `${APP_URL}/#platform` },
  };

  if (location && location.toLowerCase() !== "online") {
    eventSchema.eventAttendanceMode =
      "https://schema.org/OfflineEventAttendanceMode";
    eventSchema.location = {
      "@type": "Place",
      name: location,
      address: {
        "@type": "PostalAddress",
        addressLocality: location,
      },
    };
  } else {
    eventSchema.eventAttendanceMode =
      "https://schema.org/OnlineEventAttendanceMode";
    eventSchema.location = {
      "@type": "VirtualLocation",
      "name": "Online",
      url: `${APP_URL}/events/${slug}`,
    };
  }
  eventSchema.offers = {
    "@type": "Offer",
      "url": `${APP_URL}/events/${slug}`,
      "price": "0",
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock",
      "validFrom": dayjs(event.post_date).format("YYYY-MM-DDTHH:mm:ssZ")
  };

  if (speakers.length > 0) {
    eventSchema.performer = speakers.map((speaker) => {
      const speakerOrg =
        speaker.organization || speaker.userTaxonomy?.organization;
      const speakerName = `${speaker.first_name} ${speaker.last_name}`;
      const speakerImage =
        speaker.userTaxonomy?.image?.name ||
        speaker.profile_photo ||
        speaker.photo_url;
      const speakerJobTitle = speaker.userTaxonomy?.job_title || speaker.job_title;
      const speakerPronouns = speaker.pronoun || speaker.pronouns;
      const speakerLoc = speaker.location?.location || speaker.location;

      return {
        "@type": "Person",
        name: speakerName,
        ...(speakerImage && { image: speakerImage }),
        ...(speakerJobTitle && { jobTitle: speakerJobTitle }),
        ...(speakerPronouns && { pronouns: speakerPronouns }),
        address: {
          "@type": "PostalAddress",
          addressLocality: speakerLoc,
        },
        worksFor: speakerOrg
          ? {
            "@type": "Organization",
            "@id": `${APP_URL}/organizations/${speakerOrg.slug}/#employer`,
            name: speakerOrg.name,
          }
          : undefined,
      };
    });
  }

  if (sponsors.length > 0) {
    eventSchema.sponsor = sponsors.map((sponsor) => {
      const logoImage = sponsor?.organizationImage?.find(
        (img: OrganizationImageEntity) => img?.image?.type === "logo"
      )?.image?.name;
      return {
        "@type": "Organization",
        name: sponsor.name,
        logo: logoImage || undefined,
      };
    });
  }
  return eventSchema;
};

/**
 * Generates a schema representation for a news collection page.
 *
 * This function creates a structured data object conforming to the
 * Schema.org `CollectionPage` type. The generated schema includes details
 * about the page URL, name, description, and related entities.
 *
 * @param {WpPostEntity[]} newsItems - An array of news item entities
 * representing individual news articles to be included in the collection.
 * @param {number} totalItems - The total number of news articles available.
 * @returns {Object} The schema.org `CollectionPage` representation with
 * associated metadata for the news collection page.
 */
export const getNewsCollectionPageSchema = (
  newsItems: WpPostEntity[],
  totalItems: number
) => {
  const url = `${APP_URL}/news`;

  return {
    "@type": "CollectionPage",
    "@id": `${url}/#webpage`,
    url,
    name: "LGBTQ+ Business News & Career Insights | myGwork",
    description:
      "Stay updated with the latest LGBTQ+ business news, career advice, and culture stories.",
    isPartOf: { "@id": `${APP_URL}/#website` },
    breadcrumb: { "@id": `${url}/#breadcrumb` },
    mainEntity: getNewsArticlesSchema(newsItems, {
      name: "News",
      numberOfItems: totalItems,
    }),
  };
};

/**
 * Generates a structured schema object for a single news article, compatible with structured data for SEO purposes.
 *
 * @param {WpPostEntity} news - An object containing the properties of a WordPress post or news article entity.
 * @returns {Object} A schema object of type `NewsArticle`, with properties such as headline, article body, publication date, modification date, author, publisher, images, and article section.
 *
 * - If the `news` entity does not have the necessary fields, fallback values are used to construct the schema.
 * - Automatically formats and validates dates to ISO 8601 format.
 * - Extracts the primary image source from predefined meta keys and content data, with a fallback to a default logo.
 * - Includes a calculated category in the `articleSection` field, if available.
 */
export const getSingleNewsArticleSchema = (news: WpPostEntity) => {
  const slug = news.post_name || news.slug;
  const title = parseContent(news.post_title || news.title);
  const content = news.post_content || news.content || "";

  const formatDate = (date: any) => {
    if (!date) return undefined;
    const d = dayjs(date);
    return d.isValid() ? d.toISOString() : date;
  };

  const datePublished = formatDate(news.post_date || news.published_at);
  const dateModified = formatDate(
    news.post_modified ||
      news.updated_at ||
      news.post_date ||
      news.published_at
  );

  const schema: any = {
    "@type": "NewsArticle",
    "@id": `${APP_URL}/news/${slug}/#news`,
    headline: title,
    articleBody: stripHtml(content),
    datePublished,
    dateModified,
    author: { "@id": `${APP_URL}/#platform` },
    publisher: { "@id": `${APP_URL}/#platform` },
  };

  const images: string[] = [];
  const mainImage =
    getMetaValue(news, "_cloudinary") ||
    news.thumbnail_url ||
    news.news_thumbnail_url;
  if (mainImage) {
    images.push(mainImage);
  }

  // Extract up to 2 more images from content
  const imgRegex = /<img[^>]+src="([^">]+)"/g;
  let match: RegExpExecArray | null;

  while ((match = imgRegex.exec(content)) !== null && images.length < 3) {
    const src = match[1];

    if (src && !images.includes(src)) {
      images.push(src);
    }
  }

  schema.image = images.length > 0 ? images : [LOGO_URL];

  const categories = extractTermRelationshipByType(news, "category");
  if (categories && categories.length > 0) {
    schema.articleSection = categories[0]?.name;
  }

  return schema;
};

export const getPageSchema = (
  data: {
    name: string;
    url: string;
    description: string;
    type: string;
  } = {
    name: "",
    url: "",
    description: "",
    type: "",
  }
) => {
  return {
    ...getWebPageSchema(data.name, data.url, data.type),
  };
}

export const getMentions = (organizations: OrganizationEntity[]) =>
  organizations.map(
    (org, index) => ({
      "@type": "Organization",
      "@id": `${APP_URL}/organizations/${org.slug}/#employer`,
      name: org.name,
    })
  );
